import React from "react";
import { NeoButton, NeoInput } from "../../../../../components/Common";
import { DEFAULT_RUNNING_CLUB_SESSION, RUNNING_PACE_GROUPS } from "../../domain/running/constants";
import RunningHelpInfo from "./RunningHelpInfo";

const buildGroup = (index) => {
    const template = RUNNING_PACE_GROUPS[index] || RUNNING_PACE_GROUPS[0];
    return {
        ...template,
        id: `${template.value}_${Date.now()}`,
        value: template.value,
        label: template.label,
        desc: template.desc,
        enabled: true,
    };
};

const RunningClubSettings = ({ session = {}, namePrefix, handleChange, setFieldValue }) => {
    const clubSession = {
        ...DEFAULT_RUNNING_CLUB_SESSION,
        ...(session.clubSession || {}),
    };
    const paceGroups = clubSession.paceGroups?.length ? clubSession.paceGroups : DEFAULT_RUNNING_CLUB_SESSION.paceGroups;

    const updateClubField = (field, value) => {
        setFieldValue(`${namePrefix}.clubSession.${field}`, value);
    };

    const updateGroup = (groupIndex, field, value) => {
        setFieldValue(`${namePrefix}.clubSession.paceGroups.${groupIndex}.${field}`, value);
    };

    const addGroup = () => {
        setFieldValue(`${namePrefix}.clubSession.paceGroups`, [
            ...paceGroups,
            buildGroup(paceGroups.length),
        ]);
    };

    const removeGroup = (groupIndex) => {
        setFieldValue(`${namePrefix}.clubSession.paceGroups`, paceGroups.filter((_, index) => index !== groupIndex));
    };

    return (
        <div className="running-club-settings mb-3">
            <div className="running-collapsible-header mb-3">
                <div>
                    <h6 className="mb-0 fw-bold text-dark font-size-13 d-flex align-items-center gap-2">
                        Modo Clube
                        <span className="running-section-chip">Opcional</span>
                    </h6>
                    <span className="text-muted font-size-11">Ative apenas quando a sessão for aplicada para turma ou grupos de pace</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch mb-0">
                        <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input cursor-pointer"
                            id={`${namePrefix}.clubSession.isClubSession`}
                            checked={!!clubSession.isClubSession}
                            onChange={(event) => {
                                updateClubField("isClubSession", event.target.checked);
                            }}
                        />
                        <label className="form-check-label font-size-12 fw-semibold text-dark cursor-pointer ms-1" htmlFor={`${namePrefix}.clubSession.isClubSession`}>
                            Treino de clube
                        </label>
                    </div>
                </div>
            </div>

            {clubSession.isClubSession && (
                <>
                    <div className="row g-2 mb-3">
                        <div className="col-md-3 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Turma <RunningHelpInfo text="Nome do grupo que fará esse treino, como Manhã 5h30, Iniciantes ou Performance." /></label>
                            <NeoInput type="text" name={`${namePrefix}.clubSession.teamName`} value={clubSession.teamName || ""} onChange={handleChange} size="sm" placeholder="Manhã 5h30" />
                        </div>
                        <div className="col-md-3 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Professor <RunningHelpInfo text="Responsável por conduzir a sessão no dia do treino." /></label>
                            <NeoInput type="text" name={`${namePrefix}.clubSession.coachName`} value={clubSession.coachName || ""} onChange={handleChange} size="sm" placeholder="Responsável" />
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Horário <RunningHelpInfo text="Horário de início da sessão para alinhar comunicação e presença." /></label>
                            <NeoInput type="time" name={`${namePrefix}.clubSession.startTime`} value={clubSession.startTime || ""} onChange={handleChange} size="sm" />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Ponto de encontro <RunningHelpInfo text="Local claro para chegada dos alunos, como pista, praça, parque ou academia." /></label>
                            <NeoInput type="text" name={`${namePrefix}.clubSession.meetingPoint`} value={clubSession.meetingPoint || ""} onChange={handleChange} size="sm" placeholder="Pista, praça, parque..." />
                        </div>
                        <div className="col-md-5 col-12">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Rota <RunningHelpInfo text="Percurso ou referência do treino. Ajuda o professor a orientar grupos e hidratação." /></label>
                            <NeoInput type="text" name={`${namePrefix}.clubSession.routeName`} value={clubSession.routeName || ""} onChange={handleChange} size="sm" placeholder="Ex: Orla ida e volta, circuito parque" />
                        </div>
                        <div className="col-md-7 col-12">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Observações de segurança/logística <RunningHelpInfo text="Use para hidratação, travessias, pontos de apoio, iluminação, risco de chuva ou qualquer cuidado do treino." /></label>
                            <NeoInput type="text" name={`${namePrefix}.clubSession.safetyNotes`} value={clubSession.safetyNotes || ""} onChange={handleChange} size="sm" placeholder="Hidratação, travessias, pontos de apoio..." />
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <span className="font-size-11 fw-bold text-uppercase text-muted">Grupos de pace <RunningHelpInfo text="Crie versões do mesmo treino para níveis diferentes. Os paces desses grupos podem alimentar os segmentos e a exportação para relógio." /></span>
                        <NeoButton type="button" variant="soft-secondary" size="sm" className="running-add-pace-group" onClick={addGroup} icon="mdi-plus">
                            Grupo
                        </NeoButton>
                    </div>

                    <div className="running-pace-group-grid">
                        {paceGroups.map((group, groupIndex) => (
                            <div key={group.id || groupIndex} className="running-pace-group-card">
                                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input mt-0"
                                            checked={group.enabled !== false}
                                            onChange={(event) => updateGroup(groupIndex, "enabled", event.target.checked)}
                                        />
                                        <input
                                            type="text"
                                            value={group.label || ""}
                                            onChange={(event) => updateGroup(groupIndex, "label", event.target.value)}
                                            className="form-control form-control-sm running-pace-group-label"
                                            placeholder="G1"
                                        />
                                    </div>
                                    <NeoButton type="button" variant="soft-danger" size="sm" square icon="mdi-close" onClick={() => removeGroup(groupIndex)} />
                                </div>
                                <input
                                    type="text"
                                    value={group.desc || ""}
                                    onChange={(event) => updateGroup(groupIndex, "desc", event.target.value)}
                                    className="form-control form-control-sm mb-2 font-size-12"
                                    placeholder="Descrição do grupo"
                                />
                                <div className="row g-1">
                                    <div className="col-6">
                                        <label className="form-label font-size-9 fw-bold text-muted mb-1">Leve <RunningHelpInfo text="Pace confortável para Z1/Z2, aquecimento, recuperação e longões leves." /></label>
                                        <input type="text" className="form-control form-control-sm" value={group.easyPace || ""} onChange={(event) => updateGroup(groupIndex, "easyPace", event.target.value)} placeholder="6:00-6:30" />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label font-size-9 fw-bold text-muted mb-1">Limiar <RunningHelpInfo text="Pace forte controlado, útil para tempo run, ritmo de prova e treinos de sustentação." /></label>
                                        <input type="text" className="form-control form-control-sm" value={group.thresholdPace || ""} onChange={(event) => updateGroup(groupIndex, "thresholdPace", event.target.value)} placeholder="5:00-5:20" />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label font-size-9 fw-bold text-muted mb-1">Tiros <RunningHelpInfo text="Pace mais forte para intervalados, subidas curtas e estímulos de velocidade." /></label>
                                        <input type="text" className="form-control form-control-sm" value={group.intervalPace || ""} onChange={(event) => updateGroup(groupIndex, "intervalPace", event.target.value)} placeholder="4:30-4:50" />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label font-size-9 fw-bold text-muted mb-1">Longão <RunningHelpInfo text="Pace recomendado para treinos longos, normalmente mais controlado que o pace de limiar." /></label>
                                        <input type="text" className="form-control form-control-sm" value={group.longRunPace || ""} onChange={(event) => updateGroup(groupIndex, "longRunPace", event.target.value)} placeholder="6:20-7:00" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default RunningClubSettings;
